'use client'
import * as React from 'react'
import { ArrowLeft, ArrowRight, Check, Loader2, Plus, X, TrendingUp, Globe2, Users, ShoppingBag, Briefcase, Utensils, HeartPulse, Leaf, PawPrint, Plane, MoreHorizontal } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

// UGC ROI Calculator, 6-step wizard that estimates the monthly revenue
// uplift an Idukki deployment could deliver for a brand.
//
// Steps:
//   1. URL, we hit api.idukki.io/company/details to pull logo + social
//      handles already published on the brand's homepage.
//   2. Social audit, confirm / edit the detected handles, run
//      api.idukki.io/v2/social/details to count followers + posts and
//      convert that to a "potential UGC value" $.
//   3. Site metrics, visits, AOV, conversion rate, time on site
//      (sliders + bound inputs). Site visits are seeded from
//      api.idukki.io/company/traffic when available.
//   4. Industry, pick one. Each industry has an SOI (sales online
//      impact) % and a ROBO (research-online-buy-offline) multiplier.
//   5. Lead capture, name / company / email / job title posted to
//      api.idukki.io/user/lead. Gates the result reveal.
//   6. Results, combined SEO impact + SOI uplift + UGC value, animated
//      counters + a "talk to sales" CTA.
//
// The INDUSTRIES table and the uplift formulas (steps 3-4/6) mirror
// ROI_INDUSTRIES/roiOnsiteRevenue/roiSeoUplift/roiSoiUplift in
// nextapp/lib/publicToolsMath.ts (nextapp's white-label copy for Feefo, see
// this repo's CLAUDE.md "Public calculator tools"). The live api.idukki.io
// lookups below are the same public API both repos call — not something to
// "mirror", just shared infrastructure.

const API = 'https://api.idukki.io'

// Industry SOI (sales-online impact %) and ROBO (research-online-buy-offline)
// multipliers, pulled from the legacy WordPress page and kept identical
// so output numbers match the historical tool.
const INDUSTRIES = [
  { key: 'businessConsumerServices', label: 'Business & Consumer services', icon: Briefcase, soi: 29.62, robo: 2.17 },
  { key: 'ecommerceShopping',         label: 'Ecommerce + shopping',         icon: ShoppingBag, soi: 18.54, robo: 2.93 },
  { key: 'foodDrink',                 label: 'Food + drink',                  icon: Utensils,    soi: 21.94, robo: 3.69 },
  { key: 'health',                    label: 'Health',                        icon: HeartPulse,  soi: 7.92,  robo: 3.28 },
  { key: 'lifestyle',                 label: 'Lifestyle',                     icon: Leaf,        soi: 18.91, robo: 3.07 },
  { key: 'petsAnimals',               label: 'Pets + animals',                icon: PawPrint,    soi: 21.94, robo: 3.69 },
  { key: 'travelTourism',             label: 'Travel + tourism',              icon: Plane,       soi: 12.84, robo: 3.14 },
  { key: 'others',                    label: 'Other',                         icon: MoreHorizontal, soi: 29.82, robo: 2.77 },
] as const
type IndustryKey = (typeof INDUSTRIES)[number]['key']

type SocialPlatform = 'instagram' | 'twitter' | 'tiktok' | 'linkedin' | 'googleReview' | 'youtube' | 'pinterest' | 'threads'

const SOCIAL_META: Record<SocialPlatform, { label: string; type: string; brand: string }> = {
  instagram:    { label: 'Instagram',  type: 'LIVE_INSTAGRAM_HANDLE', brand: 'bg-gradient-to-br from-fuchsia-500 to-orange-500 text-white' },
  twitter:      { label: 'X / Twitter', type: 'LIVE_TWITTER_USERNAME', brand: 'bg-slate-900 text-white' },
  tiktok:       { label: 'TikTok',      type: 'LIVE_TIKTOK_USERNAME',  brand: 'bg-black text-white' },
  linkedin:     { label: 'LinkedIn',    type: 'LIVE_LINKEDIN_COMPANY', brand: 'bg-sky-700 text-white' },
  googleReview: { label: 'Google',      type: 'LIVE_YOUTUBE_CHANNEL',  brand: 'bg-white text-foreground border' },
  youtube:      { label: 'YouTube',     type: 'LIVE_YOUTUBE_CHANNEL',  brand: 'bg-red-600 text-white' },
  pinterest:    { label: 'Pinterest',   type: 'LIVE_YOUTUBE_CHANNEL',  brand: 'bg-red-500 text-white' },
  threads:      { label: 'Threads',     type: 'LIVE_THREAD_USERNAME',  brand: 'bg-zinc-900 text-white' },
}

type SocialEntry = { platform: SocialPlatform; handle: string; followers?: number; posts?: number }
type Metrics = { visits: number; aov: number; conversion: number; timeOnSite: number }
type Lead = { firstName: string; lastName: string; email: string; companyName: string; jobTitle: string }

function fmt(n: number): string {
  return new Intl.NumberFormat('en-US').format(Math.round(n))
}

function compact(n: number): string {
  if (n < 1000) return String(Math.round(n))
  if (n < 1_000_000) return (n / 1000).toFixed(n < 10_000 ? 1 : 0) + 'K'
  if (n < 1_000_000_000) return (n / 1_000_000).toFixed(n < 10_000_000 ? 1 : 0) + 'M'
  return (n / 1_000_000_000).toFixed(1) + 'B'
}

function extractHandle(url: string): string {
  if (!url) return ''
  try {
    // already a bare handle
    if (!url.includes('/') && !url.includes('.')) return url.startsWith('@') ? url : '@' + url
    const u = new URL(url.startsWith('http') ? url : 'https://' + url)
    const path = u.pathname.replace(/^\/+|\/+$/g, '')
    if (u.hostname.includes('linkedin.com')) {
      const handle = path.replace(/^(company|in)\//, '').split('/')[0]
      return '@' + handle.replace(/-/g, '')
    }
    if (u.hostname.includes('youtube.com')) {
      if (path.startsWith('channel/')) return path.replace('channel/', '')
      return '@' + path.replace(/^@/, '').split('/')[0]
    }
    const handle = path.split('/')[0].replace(/^@/, '')
    return '@' + handle
  } catch {
    return url
  }
}

export function UgcRoiCalculator() {
  const [step, setStep] = React.useState<1 | 2 | 3 | 4 | 5 | 6>(1)
  const [url, setUrl] = React.useState('')
  const [companyName, setCompanyName] = React.useState('')
  const [logoUrl, setLogoUrl] = React.useState<string | null>(null)
  const [socials, setSocials] = React.useState<SocialEntry[]>([])
  const [metrics, setMetrics] = React.useState<Metrics>({ visits: 50_000, aov: 80, conversion: 1.5, timeOnSite: 90 })
  const [industry, setIndustry] = React.useState<IndustryKey | null>(null)
  const [lead, setLead] = React.useState<Lead>({ firstName: '', lastName: '', email: '', companyName: '', jobTitle: '' })

  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [potentialUgcValue, setPotentialUgcValue] = React.useState(0)
  const [addSocialOpen, setAddSocialOpen] = React.useState(false)

  // Derived computations live for steps 5+
  const onsiteRevenue = React.useMemo(
    () => Math.round(metrics.aov * (metrics.conversion / 100) * metrics.visits),
    [metrics],
  )
  const industryDef = industry ? INDUSTRIES.find((i) => i.key === industry) : null
  const seoUplift = Math.round(onsiteRevenue * 0.0075)
  const soiUplift = industryDef ? Math.round((onsiteRevenue * industryDef.soi) / 100) : 0
  const totalUplift = seoUplift + soiUplift + potentialUgcValue

  // Step 1: lookup company by URL
  async function lookupCompany() {
    if (!url.trim()) { setError('Enter a URL.'); return }
    setError(null); setLoading(true)
    try {
      const clean = url.replace(/^https?:\/\//, '').replace(/\/+$/, '')
      const r = await fetch(`${API}/company/details?url=${encodeURIComponent(clean)}`)
      if (!r.ok) throw new Error('Lookup failed')
      const data = await r.json()
      const d = data?.data || {}
      setCompanyName(d.companyName || '')
      setLogoUrl(d.logo?.url || null)
      const detected: SocialEntry[] = []
      const links = d.social_media_links || {}
      for (const platform of Object.keys(SOCIAL_META) as SocialPlatform[]) {
        const urls = links[platform]
        if (Array.isArray(urls) && urls[0]) {
          detected.push({ platform, handle: extractHandle(urls[0]) })
        }
      }
      setSocials(detected)
      setStep(2)
    } catch {
      // Soft-fail to step 2, user can add socials manually
      setStep(2)
    } finally {
      setLoading(false)
    }
  }

  // Step 2 → 3: validate socials, fetch follower counts, compute UGC value
  async function validateSocialsAndFetchTraffic() {
    setError(null); setLoading(true)
    try {
      // 1) follower / posts lookup
      let ugc = 0
      const entries = socials.filter((s) => s.handle.trim().length > 0)
      if (entries.length) {
        try {
          const body = JSON.stringify({
            data: entries.map((s) => ({
              type: SOCIAL_META[s.platform].type,
              metaId: s.handle.replace(/^[@#]/, ''),
            })),
          })
          const r = await fetch(`${API}/v2/social/details`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
          })
          const data = await r.json()
          const enriched = entries.map((s) => {
            const found = (data?.result || []).find((x: { type: string }) => x.type === SOCIAL_META[s.platform].type)
            const followers = found?.followers ? Number(found.followers) : undefined
            const posts = found?.media_count ? Number(found.media_count) : undefined
            if (followers) ugc += Math.round(0.01 * followers)
            if (posts)     ugc += Math.round(0.1  * posts)
            return { ...s, followers, posts }
          })
          setSocials(enriched)
        } catch { /* non-fatal */ }
      }
      setPotentialUgcValue(ugc)

      // 2) traffic seed (best-effort)
      try {
        const clean = url.replace(/^https?:\/\//, '').replace(/\/+$/, '')
        const r = await fetch(`${API}/company/traffic?url=${encodeURIComponent(clean)}`)
        const data = await r.json()
        if (data?.status && data?.result?.Engagments) {
          const eg = data.result.Engagments
          setMetrics((m) => ({
            ...m,
            visits: Math.round(Number(eg.Visits) || m.visits),
            timeOnSite: Math.round(Number(eg.TimeOnSite) || m.timeOnSite),
          }))
        }
      } catch { /* non-fatal */ }

      setStep(3)
    } finally {
      setLoading(false)
    }
  }

  // Step 5: submit lead, then reveal results
  async function submitLead() {
    if (!lead.firstName || !lead.lastName || !lead.email || !lead.companyName) {
      setError('Please fill every field.')
      return
    }
    setError(null); setLoading(true)
    try {
      await fetch(`${API}/user/lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: lead.firstName,
          last_name: lead.lastName,
          email: lead.email,
          job_title: lead.jobTitle,
          business_name: lead.companyName,
        }),
      })
    } catch { /* swallow, we still reveal results */ }
    setLoading(false)
    setStep(6)
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_minmax(0,520px)]">
      {/* ── Left: framing + live result card ─────────────────────────────── */}
      <div className="space-y-6">
        <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6 text-white shadow-xl md:p-8">
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-emerald-300/90">Live projection</p>
          <h3 className="mt-2 font-display text-2xl font-semibold leading-tight md:text-3xl">
            Estimate the UGC uplift on your monthly revenue.
          </h3>
          <p className="mt-3 text-sm leading-relaxed text-white/70">
            12 months of benchmarking data, plus your inputs. We project SEO lift,
            on-site conversion impact and the latent value sitting inside your
            social channels.
          </p>

          <div className="mt-6 space-y-3">
            <LiveStat label="Current on-site revenue / mo" value={`$${fmt(onsiteRevenue)}`} hint="AOV × conversion × visits" />
            {potentialUgcValue > 0 && <LiveStat label="Latent UGC value" value={`+$${fmt(potentialUgcValue)}`} hint="From your social audit" accent />}
            {industryDef && <LiveStat label="Total projected lift" value={`+$${fmt(totalUplift)}`} hint={`SEO 0.75% + SOI ${industryDef.soi}% + UGC`} accent="emerald" />}
          </div>

          <div className="mt-6 rounded-xl bg-white/5 p-4 ring-1 ring-white/10">
            <p className="text-xs leading-relaxed text-white/70">
              Integrated with every social platform. Unlimited sources. AI tagging on
              every asset. Idukki turns your UGC pipeline from manual upkeep into a
              compounding asset.
            </p>
            <Button asChild size="sm" className="mt-4 bg-emerald-400 text-slate-900 hover:bg-emerald-300">
              <a href="https://app.idukki.io/register" target="_blank" rel="noopener noreferrer">
                Get started <ArrowRight className="ml-1 h-3.5 w-3.5" />
              </a>
            </Button>
          </div>
        </div>

        <StepPager step={step} />
      </div>

      {/* ── Right: wizard card ──────────────────────────────────────────── */}
      <div className="rounded-2xl border bg-card shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div className="text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">Step {step} of 6</div>
          {step > 1 && step < 6 && (
            <button
              onClick={() => setStep((s) => (s > 1 ? ((s - 1) as 1 | 2 | 3 | 4 | 5 | 6) : s))}
              className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-3 w-3" /> Back
            </button>
          )}
        </div>

        <div className="space-y-5 p-6 md:p-8">
          {step === 1 && (
            <>
              <h3 className="font-display text-2xl font-semibold tracking-tight">What&rsquo;s your company URL?</h3>
              <p className="text-sm text-muted-foreground">We&rsquo;ll pull your logo, social handles and recent traffic to seed the calculator.</p>
              <div className="relative">
                <Globe2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="glossier.com"
                  className="pl-9"
                  onKeyDown={(e) => { if (e.key === 'Enter') lookupCompany() }}
                />
              </div>
              <NavRow loading={loading} disabled={!url.trim() || loading} onNext={lookupCompany} label="Continue" />
            </>
          )}

          {step === 2 && (
            <>
              <h3 className="font-display text-2xl font-semibold tracking-tight">{companyName || 'Your brand'}&rsquo;s social footprint</h3>
              <p className="text-sm text-muted-foreground">Confirm what we detected, or add anything missing.</p>

              {logoUrl && (
                <div className="flex items-center gap-3 rounded-lg border bg-slate-50 p-3">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={logoUrl} alt="" className="h-8 w-auto max-w-[120px] object-contain" />
                  <span className="text-sm font-medium">{companyName}</span>
                </div>
              )}

              <ul className="space-y-2">
                {socials.map((s, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-xs font-bold', SOCIAL_META[s.platform].brand)}>
                      {SOCIAL_META[s.platform].label.slice(0, 2).toUpperCase()}
                    </span>
                    <Input
                      value={s.handle}
                      onChange={(e) => setSocials(socials.map((x, j) => j === i ? { ...x, handle: e.target.value } : x))}
                      placeholder={`@${SOCIAL_META[s.platform].label.toLowerCase()} handle`}
                      className="flex-1"
                    />
                    <button
                      type="button"
                      onClick={() => setSocials(socials.filter((_, j) => j !== i))}
                      className="rounded-md p-2 text-muted-foreground hover:bg-secondary hover:text-foreground"
                      aria-label="Remove"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>

              {!addSocialOpen ? (
                <button
                  type="button"
                  onClick={() => setAddSocialOpen(true)}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                >
                  <Plus className="h-3.5 w-3.5" /> Add platform
                </button>
              ) : (
                <div className="rounded-lg border bg-slate-50 p-3">
                  <p className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">Pick a platform</p>
                  <div className="flex flex-wrap gap-2">
                    {(Object.keys(SOCIAL_META) as SocialPlatform[]).map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => { setSocials([...socials, { platform: p, handle: '' }]); setAddSocialOpen(false) }}
                        className={cn('rounded-full px-3 py-1 text-xs font-medium transition', SOCIAL_META[p].brand, 'hover:brightness-110')}
                      >
                        {SOCIAL_META[p].label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <NavRow loading={loading} onNext={validateSocialsAndFetchTraffic} label="Validate + continue" />
            </>
          )}

          {step === 3 && (
            <>
              <h3 className="font-display text-2xl font-semibold tracking-tight">Tell us about your storefront</h3>
              <p className="text-sm text-muted-foreground">Tweak the seeded numbers if you have better data, the live panel updates instantly.</p>

              <SliderRow label="Monthly site visits" hint="Sessions to your storefront" min={0} max={5_000_000} step={500} value={metrics.visits} onChange={(v) => setMetrics({ ...metrics, visits: v })} format={(v) => compact(v)} />
              <SliderRow label="Average order value" hint="What an average basket spends" min={0} max={1000} step={1} value={metrics.aov} onChange={(v) => setMetrics({ ...metrics, aov: v })} format={(v) => '$' + v} />
              <SliderRow label="Conversion rate" hint="Visitors that purchase" min={0} max={10} step={0.1} value={metrics.conversion} onChange={(v) => setMetrics({ ...metrics, conversion: v })} format={(v) => v.toFixed(1) + '%'} />
              <SliderRow label="Avg time on site" hint="Seconds per session" min={0} max={600} step={1} value={metrics.timeOnSite} onChange={(v) => setMetrics({ ...metrics, timeOnSite: v })} format={(v) => v + 's'} />

              <NavRow onNext={() => setStep(4)} label="Continue" disabled={metrics.aov === 0 || metrics.conversion === 0 || metrics.visits === 0} />
            </>
          )}

          {step === 4 && (
            <>
              <h3 className="font-display text-2xl font-semibold tracking-tight">Which industry are you in?</h3>
              <p className="text-sm text-muted-foreground">Each category has its own SOI (sales-online impact) benchmark.</p>
              <ul className="grid grid-cols-2 gap-2">
                {INDUSTRIES.map((ind) => {
                  const Icon = ind.icon
                  const active = industry === ind.key
                  return (
                    <li key={ind.key}>
                      <button
                        type="button"
                        onClick={() => setIndustry(ind.key)}
                        className={cn(
                          'flex h-24 w-full flex-col items-center justify-center gap-2 rounded-xl border p-3 text-center text-xs font-medium transition',
                          active ? 'border-foreground bg-foreground text-background' : 'border-input bg-card text-foreground hover:border-foreground/40',
                        )}
                      >
                        <Icon className="h-5 w-5" />
                        {ind.label}
                      </button>
                    </li>
                  )
                })}
              </ul>
              <NavRow onNext={() => setStep(5)} disabled={!industry} label="Calculate" />
            </>
          )}

          {step === 5 && (
            <>
              <h3 className="font-display text-2xl font-semibold tracking-tight">One step away from your numbers.</h3>
              <p className="text-sm text-muted-foreground">We&rsquo;ll send your projection summary to this email and keep it on file for your free trial.</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <LeadField label="First name"     value={lead.firstName}   onChange={(v) => setLead({ ...lead, firstName: v })} />
                <LeadField label="Last name"      value={lead.lastName}    onChange={(v) => setLead({ ...lead, lastName: v })} />
                <LeadField label="Business email" value={lead.email}       onChange={(v) => setLead({ ...lead, email: v })} type="email" full />
                <LeadField label="Company name"   value={lead.companyName || companyName} onChange={(v) => setLead({ ...lead, companyName: v })} />
                <LeadField label="Job title"      value={lead.jobTitle}    onChange={(v) => setLead({ ...lead, jobTitle: v })} />
              </div>
              {error && <p className="text-sm text-rose-600">{error}</p>}
              <NavRow loading={loading} onNext={submitLead} label="Reveal projection" icon={<TrendingUp className="ml-1.5 h-4 w-4" />} />
            </>
          )}

          {step === 6 && industryDef && (
            <>
              <div className="flex items-center gap-2 text-emerald-600">
                <Check className="h-5 w-5" />
                <span className="text-xs font-semibold uppercase tracking-wider">Your projection</span>
              </div>
              <h3 className="font-display text-2xl font-semibold tracking-tight">{companyName || 'Your brand'} on Idukki</h3>
              <p className="text-sm text-muted-foreground">Combined monthly revenue uplift based on your inputs and Idukki benchmarks.</p>

              <ResultRow label="SEO lift"
                hint="0.75% organic search uplift from indexed UGC"
                value={`+$${fmt(seoUplift)}`} />
              <ResultRow label="On-site conversion impact"
                hint={`${industryDef.soi}% lift on ${industryDef.label.toLowerCase()}`}
                value={`+$${fmt(soiUplift)}`} />
              {potentialUgcValue > 0 && (
                <ResultRow label="Latent UGC value"
                  hint={`From ${socials.filter((s) => s.followers || s.posts).length} active channels`}
                  value={`+$${fmt(potentialUgcValue)}`} />
              )}

              <div className="mt-2 rounded-2xl bg-foreground p-5 text-background">
                <p className="text-xs font-medium uppercase tracking-[0.2em] opacity-70">Total projected uplift</p>
                <p className="mt-1 font-display text-3xl font-semibold">+${fmt(totalUplift)} <span className="text-base font-normal opacity-70">/ mo</span></p>
                <p className="mt-1 text-xs opacity-70">Combined online + offline (ROBO {industryDef.robo}× multiplier on offline visits).</p>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row">
                <Button asChild className="flex-1">
                  <a href="https://app.idukki.io/register" target="_blank" rel="noopener noreferrer">Start your 14-day trial</a>
                </Button>
                <Button asChild variant="outline" className="flex-1">
                  <a href="https://app.apollo.io/#/meet/idukki/Rohin" target="_blank" rel="noopener noreferrer">Book a strategy call</a>
                </Button>
              </div>
            </>
          )}

          {error && step !== 5 && <p className="text-sm text-rose-600">{error}</p>}
        </div>
      </div>
    </div>
  )
}

function NavRow({ onNext, label, disabled, loading, icon }: { onNext: () => void; label: string; disabled?: boolean; loading?: boolean; icon?: React.ReactNode }) {
  return (
    <div className="pt-2">
      <Button onClick={onNext} disabled={disabled || loading} className="w-full sm:w-auto">
        {loading ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Working…</> : <>{label}{icon ?? <ArrowRight className="ml-1.5 h-4 w-4" />}</>}
      </Button>
    </div>
  )
}

function LiveStat({ label, value, hint, accent }: { label: string; value: string; hint?: string; accent?: 'emerald' | true }) {
  return (
    <div className={cn(
      'rounded-lg p-3 ring-1 ring-inset transition',
      accent === 'emerald' ? 'bg-emerald-400/10 ring-emerald-400/30' :
      accent ? 'bg-white/5 ring-white/20' :
      'bg-white/5 ring-white/10',
    )}>
      <div className="text-[11px] uppercase tracking-wider text-white/75">{label}</div>
      <div className={cn('font-display text-xl font-semibold', accent === 'emerald' ? 'text-emerald-300' : 'text-white')}>{value}</div>
      {hint && <div className="text-[10px] text-white/75">{hint}</div>}
    </div>
  )
}

function StepPager({ step }: { step: number }) {
  return (
    <div className="flex items-center gap-2 px-2 text-xs text-muted-foreground">
      {[1, 2, 3, 4, 5, 6].map((s) => (
        <span
          key={s}
          className={cn(
            'h-1 flex-1 rounded-full transition',
            s < step ? 'bg-emerald-500' : s === step ? 'bg-foreground' : 'bg-border',
          )}
        />
      ))}
    </div>
  )
}

function SliderRow({ label, hint, min, max, step, value, onChange, format }: {
  label: string; hint?: string
  min: number; max: number; step: number
  value: number; onChange: (v: number) => void
  format: (v: number) => string
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-sm font-medium">{label}</div>
          {hint && <div className="text-[11px] text-muted-foreground">{hint}</div>}
        </div>
        <div className="rounded-md border bg-card px-2 py-0.5 font-mono text-sm font-semibold">{format(value)}</div>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-foreground"
      />
      <div className="flex justify-between text-[10px] text-muted-foreground">
        <span>{format(min)}</span>
        <span>{format(max)}</span>
      </div>
    </div>
  )
}

function LeadField({ label, value, onChange, type = 'text', full }: { label: string; value: string; onChange: (v: string) => void; type?: string; full?: boolean }) {
  return (
    <label className={cn('block', full && 'sm:col-span-2')}>
      <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  )
}

function ResultRow({ label, hint, value }: { label: string; hint?: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border bg-card p-3">
      <div>
        <div className="text-sm font-semibold">{label}</div>
        {hint && <div className="text-[11px] text-muted-foreground">{hint}</div>}
      </div>
      <div className="font-display text-lg font-semibold text-emerald-600">{value}</div>
    </div>
  )
}

// Suppress unused-import warning for icons referenced only via INDUSTRIES table
void Users
